import { useRef, useCallback } from 'react';

// ===== HELPER: Distance between two touch points =====
function getTouchDistance(t1: React.Touch | Touch, t2: React.Touch | Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ===== HELPER: Midpoint of two touch points =====
function getTouchMidpoint(t1: React.Touch | Touch, t2: React.Touch | Touch): { x: number; y: number } {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

// ===== HOOK: Pinch-to-zoom for images =====
export function usePinchZoom(containerRef: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
  const scaleRef = useRef(1);
  const originRef = useRef({ x: 50, y: 50 });
  const startDistRef = useRef(0);
  const startScaleRef = useRef(1);
  const isPinchingRef = useRef(false);

  const applyTransform = useCallback((el: HTMLDivElement) => {
    // Target the active Swiper slide, not the first image in the entire container
    const activeSlide = el.querySelector('.swiper-slide-active');
    const target = activeSlide || el;
    // Apply to ALL images in the active slide (thumbnail + full-res layers in ProgressiveSlideImage)
    const imgs = target.querySelectorAll('img, video') as NodeListOf<HTMLElement>;
    if (imgs.length === 0) return;
    const s = scaleRef.current;
    imgs.forEach(img => {
      img.style.transformOrigin = `${originRef.current.x}% ${originRef.current.y}%`;
      img.style.transform = s > 1 ? `scale(${s})` : '';
      img.style.transition = isPinchingRef.current ? 'none' : 'transform 200ms ease';
    });
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;
    if (e.touches.length === 2) {
      isPinchingRef.current = true;
      startDistRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      startScaleRef.current = scaleRef.current;

      // Compute origin relative to container
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const mid = getTouchMidpoint(e.touches[0], e.touches[1]);
        originRef.current = {
          x: ((mid.x - rect.left) / rect.width) * 100,
          y: ((mid.y - rect.top) / rect.height) * 100,
        };
      }
    }
  }, [enabled, containerRef]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || !isPinchingRef.current || e.touches.length < 2) return;
    const dist = getTouchDistance(e.touches[0], e.touches[1]);
    const newScale = Math.min(4, Math.max(1, startScaleRef.current * (dist / startDistRef.current)));
    scaleRef.current = newScale;

    const el = containerRef.current;
    if (el) applyTransform(el);
  }, [enabled, containerRef, applyTransform]);

  const handleTouchEnd = useCallback(() => {
    if (!isPinchingRef.current) return;
    isPinchingRef.current = false;
    // Snap back if near 1
    if (scaleRef.current < 1.1) {
      scaleRef.current = 1;
    }
    const el = containerRef.current;
    if (el) applyTransform(el);
  }, [containerRef, applyTransform]);

  const resetZoom = useCallback(() => {
    scaleRef.current = 1;
    isPinchingRef.current = false;
    const el = containerRef.current;
    if (el) {
      // Reset ALL images across all slides to clear any lingering zoom
      const imgs = el.querySelectorAll('img, video') as NodeListOf<HTMLElement>;
      imgs.forEach(img => {
        img.style.transform = '';
        img.style.transition = 'transform 200ms ease';
      });
    }
  }, [containerRef]);

  return {
    pinchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isPinching: isPinchingRef,
    resetZoom,
  };
}
